import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { API_URL } from '../config';
import { situacaoExameLabel, situacaoExameCores, situacaoExameMap } from '../utils/situacaoExame';
import gerarLaudoPDF from '../utils/gerarLaudoPDF';
import gerarHashVerificacao from '../utils/hashVerificacao';
import RUBRICA_BASE64 from '../utils/rubricaBase64';
import { formatDate, formatDateTime } from '../utils/formatDate';
import './ResultadoDetalhes.css';

function limparNomeExame(nome) {
  if (!nome) return '';
  let n = nome;
  n = n.replace(/\s*-\s*(ITA|HP|CAM|PED|LAG|LAGOA)\s*$/i, '');
  n = n.replace(/\s+LAB\s+REDE\b.*$/i, '');
  n = n.replace(/\s*\((SANGUE|SORO|PLASMA)\)\s*$/i, '');
  n = n.replace(/[\s,\-]+$/, '').trim();
  n = n.toLowerCase().replace(/(^|[\s,/(-])([a-zA-Z])/g, (m, pre, c) => pre + c.toUpperCase());
  return n;
}

function ResultadoDetalhes() {
  const { atendimentoId } = useParams();
  const navigate = useNavigate();
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historicos, setHistoricos] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroColeta, setFiltroColeta] = useState('todos');
  const [collapsedExames, setCollapsedExames] = useState(new Set());

  useEffect(() => {
    fetchResultado();
  }, [atendimentoId]);

  const fetchResultado = async () => {
    try {
      const response = await axios.get(`${API_URL}/resultados/${atendimentoId}`);
      setResultado(response.data);
    } catch (error) {
      console.error('Erro ao buscar resultado:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorico = async (pacienteId, componenteId) => {
    if (historicos[componenteId]) return;
    try {
      const response = await axios.get(`${API_URL}/historico-resultado/${pacienteId}/${componenteId}`);
      if (response.data.length > 0) {
        setHistoricos(prev => ({
          ...prev,
          [componenteId]: response.data.reverse()
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
    }
  };

  // formatDate e formatDateTime importados de ../utils/formatDate

  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return '-';
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) idade--;
    const sexo = resultado?.PACIENTE_SEXO;
    return `${idade} anos${sexo ? ` - ${sexo === 'M' ? 'Masculino' : sexo === 'F' ? 'Feminino' : sexo}` : ''}`;
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const abrirPDFOriginal = async (local, protocolo) => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API_URL}/laudos/download/${local}/${protocolo}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Erro ao abrir PDF';
      alert(typeof msg === 'string' ? msg : 'Erro ao abrir PDF do laudo');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const abrirPDFExame = async (exameId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/solicitacao-exames/${exameId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao carregar PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Erro ao abrir PDF:', error);
      alert('Erro ao carregar PDF');
    }
  };

  const renderLabHeader = () => (
    <div className="laudo-header">
      <div className="lab-logo">
        <img src="/logosantarita.png" alt="Santa Rita" className="lab-logo-img" />
      </div>
      <div className="lab-info">
        <div className="lab-addresses">
          <p><strong>Matriz</strong> - Av. Ribeiro Pena, 301A - Centro - Itapecerica/MG - CEP: 35.550-000</p>
          <p className="lab-reg">CRF/MG 42.189 &nbsp; CNES: 0483907</p>
          <p><strong>Filial</strong> - Rua Arlindo Isaias, 07 - Centro - Camacho/MG - CEP: 35.555-000</p>
          <p className="lab-reg">CRF/MG 44.018 &nbsp; CNES: 3352412</p>
          <p><strong>Filial</strong> - Praça Gabriel Resende Passos, 60 - Centro - Pedra do Indaiá/MG - CEP: 35.565-000</p>
          <p className="lab-reg">CRF/MG 47.706 &nbsp; CNES: 5810086</p>
        </div>
        <p className="lab-phone">Fone / WhatsApp: (37) 3341-1460</p>
      </div>
      <div className="lab-certifications">
        <div className="cert-badge">PNCQ</div>
        <div className="cert-badge">SBAC</div>
      </div>
    </div>
  );

  const renderPatientInfo = () => (
    <div className="patient-info">
      <div className="patient-row">
        <div className="patient-field">
          <span className="patient-label">Paciente:</span>
          <span className="patient-value">{resultado.PACIENTE_NOME || '-'}</span>
        </div>
        <div className="patient-field">
          <span className="patient-label">Idade/Sexo:</span>
          <span className="patient-value">{calcularIdade(resultado.PACIENTE_DATANASCIMENTO)}</span>
        </div>
        <div className="patient-field">
          <span className="patient-label">DN:</span>
          <span className="patient-value">{formatDate(resultado.PACIENTE_DATANASCIMENTO)}</span>
        </div>
        {(resultado.PACIENTE_MATRICULA || resultado.PACIENTE_IDENTIDADE) && (
          <div className="patient-field">
            <span className="patient-value patient-code">{resultado.PACIENTE_MATRICULA || resultado.PACIENTE_IDENTIDADE}</span>
          </div>
        )}
      </div>

      <div className="patient-row">
        <div className="patient-field">
          <span className="patient-label">CPF:</span>
          <span className="patient-value">{formatCPF(resultado.PACIENTE_CPF)}</span>
        </div>
        {resultado.PACIENTE_TELEFONE && (
          <div className="patient-field">
            <span className="patient-label">Telefone:</span>
            <span className="patient-value">{resultado.PACIENTE_TELEFONE}</span>
          </div>
        )}
      </div>

      {resultado.PACIENTE_ENDERECO && (
        <div className="patient-row">
          <div className="patient-field">
            <span className="patient-label">Endereço:</span>
            <span className="patient-value">
              {[
                resultado.PACIENTE_ENDERECO,
                resultado.PACIENTE_NUMERO ? `Nº ${resultado.PACIENTE_NUMERO}` : null,
                resultado.PACIENTE_COMPLEMENTO,
                resultado.PACIENTE_BAIRRO,
                resultado.PACIENTE_CIDADE && resultado.PACIENTE_UF
                  ? `${resultado.PACIENTE_CIDADE}/${resultado.PACIENTE_UF}`
                  : resultado.PACIENTE_CIDADE,
                resultado.PACIENTE_CEP ? `CEP: ${resultado.PACIENTE_CEP}` : null
              ].filter(Boolean).join(', ')}
            </span>
          </div>
        </div>
      )}

      {resultado.PACIENTE_EMAIL && (
        <div className="patient-row">
          <div className="patient-field">
            <span className="patient-label">E-mail:</span>
            <span className="patient-value">{resultado.PACIENTE_EMAIL}</span>
          </div>
        </div>
      )}

      <hr className="patient-separator" />

      <div className="patient-row">
        <div className="patient-field">
          <span className="patient-label">Médico(a):</span>
          <span className="patient-value">{resultado.MEDICO_NOME || '-'}</span>
        </div>
        {resultado.CRM && (
          <div className="patient-field">
            <span className="patient-label">CRM:</span>
            <span className="patient-value">{resultado.CRM}{resultado.UF_CRM ? `/${resultado.UF_CRM}` : ''}</span>
          </div>
        )}
      </div>

      <hr className="patient-separator" />

      <div className="patient-row">
        <div className="patient-field">
          <span className="patient-label">Protocolo:</span>
          <span className="patient-value highlight">{resultado.PROTOCOLO || '-'}</span>
        </div>
        <div className="patient-field">
          <span className="patient-label">Atendimento:</span>
          <span className="patient-value">{resultado.ID}</span>
        </div>
        <div className="patient-field">
          <span className="patient-label">Data:</span>
          <span className="patient-value">{formatDate(resultado.DATA)}</span>
        </div>
        {resultado.DATAENTREGA && (
          <div className="patient-field">
            <span className="patient-label">Entrega:</span>
            <span className="patient-value">{formatDate(resultado.DATAENTREGA)}</span>
          </div>
        )}
      </div>

      {resultado.CONVENIO && (
        <div className="patient-row">
          <div className="patient-field">
            <span className="patient-label">Convênio:</span>
            <span className="patient-value">{resultado.CONVENIO_NOME || resultado.CONVENIO}</span>
          </div>
          {resultado.PLANO && (
            <div className="patient-field">
              <span className="patient-label">Plano:</span>
              <span className="patient-value">{resultado.PLANO}</span>
            </div>
          )}
          {resultado.CARTEIRINHA && (
            <div className="patient-field">
              <span className="patient-label">Carteirinha:</span>
              <span className="patient-value">{resultado.CARTEIRINHA}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderGrafico = (componenteId, componente, unidade) => {
    const historico = historicos[componenteId];
    if (!historico || historico.length < 2) return null;

    const data = historico.map(h => ({
      data: new Date(h.DATACOLETA || h.DATA_ATENDIMENTO).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      valor: parseFloat(String(h.RESULTADO).replace(',', '.')),
      dataCompleta: new Date(h.DATACOLETA || h.DATA_ATENDIMENTO).toLocaleDateString('pt-BR')
    }));

    return (
      <div className="historico-chart">
        <h4>Histórico de Resultados</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="data" style={{ fontSize: '0.7rem' }} />
            <YAxis style={{ fontSize: '0.7rem' }} domain={[0, 'auto']} />
            <Tooltip
              formatter={(value) => [`${value} ${unidade || ''}`, 'Resultado']}
              labelFormatter={(label, payload) => payload && payload[0] ? payload[0].payload.dataCompleta : label}
            />
            <Line
              type="monotone"
              dataKey="valor"
              stroke="#1976d2"
              strokeWidth={2}
              dot={{ fill: '#1976d2', r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginTop: '8px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#666', fontWeight: '600', fontSize: '0.72rem' }}>Data</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#666', fontWeight: '600', fontSize: '0.72rem' }}>Resultado</th>
              {unidade && <th style={{ textAlign: 'left', padding: '6px 8px', color: '#666', fontWeight: '600', fontSize: '0.72rem' }}>Unidade</th>}
              <th style={{ textAlign: 'center', padding: '6px 8px', color: '#666', fontWeight: '600', fontSize: '0.72rem' }}>Variação</th>
            </tr>
          </thead>
          <tbody>
            {data.slice().reverse().map((row, i, arr) => {
              const prev = i < arr.length - 1 ? arr[i + 1].valor : null;
              const diff = prev !== null ? row.valor - prev : null;
              const diffPct = prev !== null && prev !== 0 ? ((diff / prev) * 100) : null;
              const diffColor = diff === null ? '#999' : diff > 0 ? '#c62828' : diff < 0 ? '#2e7d32' : '#666';
              const isLast = i === 0;
              return (
                <tr key={i} style={{
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: isLast ? '#f0f7ff' : 'transparent',
                }}>
                  <td style={{ padding: '5px 8px', fontWeight: isLast ? '600' : '400' }}>{row.dataCompleta}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: '600', fontFamily: 'monospace' }}>{row.valor}</td>
                  {unidade && <td style={{ padding: '5px 8px', color: '#888' }}>{unidade}</td>}
                  <td style={{ padding: '5px 8px', textAlign: 'center', color: diffColor, fontSize: '0.75rem' }}>
                    {diff !== null ? `${diff > 0 ? '+' : ''}${diff.toFixed(2)}${diffPct !== null ? ` (${diffPct > 0 ? '+' : ''}${diffPct.toFixed(1)}%)` : ''}` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderResponsavel = (exame) => {
    const nome = exame.RESPONSAVEL_NOME ? exame.RESPONSAVEL_NOME.trim() : 'Lucas Pedrosa Gussen';
    const crf = exame.RESPONSAVEL_CRF ? exame.RESPONSAVEL_CRF.trim() : '23038';
    const conselho = exame.RESPONSAVEL_CONSELHO ? exame.RESPONSAVEL_CONSELHO.trim() : 'CRF';
    const uf = exame.RESPONSAVEL_UF ? exame.RESPONSAVEL_UF.trim() : 'MG';
    const rtHash = gerarHashVerificacao(`${resultado.PROTOCOLO}-${nome}-${crf}`).toUpperCase();

    return (
      <div className="exam-responsavel">
        <div className="responsavel-content">
          <div className="responsavel-left">
            <p className="responsavel-rt-label">RT - {nome} - {conselho}/{uf} n&ordm;{' '}{crf}</p>
            <p className="responsavel-hash">{rtHash}</p>
          </div>
          <div className="responsavel-right">
            <img src={RUBRICA_BASE64} alt="Assinatura" className="responsavel-rubrica" />
            <p className="responsavel-nome">{nome}</p>
            <p className="responsavel-crf">{conselho} - {uf} &nbsp;&nbsp; {crf}</p>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="laudo-loading">Carregando laudo...</div>;
  if (!resultado) return <div className="laudo-loading">Laudo não encontrado</div>;

  return (
    <div className="laudo-container">
      <div className="laudo-toolbar">
        <button onClick={() => navigate('/resultados')} className="btn-back">
          ← Voltar
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => navigate(`/atendimentos/${resultado.ID}`)} className="btn-back" style={{ marginRight: '4px' }}>
            Ver Atendimento
          </button>
          {resultado.RESULTADO_LIBERADO === 'T' && resultado.LOCAL && resultado.PROTOCOLO && (
            <button
              onClick={() => abrirPDFOriginal(resultado.LOCAL, resultado.PROTOCOLO)}
              className="btn-pdf-wmi"
              disabled={downloadingPdf}
            >
              {downloadingPdf ? 'Abrindo...' : 'PDF Original'}
            </button>
          )}
          <button onClick={() => gerarLaudoPDF(resultado)} className="btn-print">
            Gerar PDF
          </button>
        </div>
      </div>

      <div className="laudo-page">
        {renderLabHeader()}
        <hr className="laudo-divider" />
        {renderPatientInfo()}
        <hr className="laudo-divider" />

        {/* Filtros de status + coleta */}
        {resultado.exames && resultado.exames.length > 0 && (() => {
          const exames = resultado.exames;
          // Contagem por status
          const statusCount = {};
          exames.forEach(ex => {
            const sit = (ex.SITUACAO || '').trim() || 'P';
            statusCount[sit] = (statusCount[sit] || 0) + 1;
          });
          // Contagem por coleta
          const colhidos = exames.filter(ex => (ex.MATERIALCOLHIDO || '').trim() === 'T').length;
          const naoColhidos = exames.length - colhidos;

          const toggleCollapseAll = () => {
            if (collapsedExames.size > 0) {
              setCollapsedExames(new Set());
            } else {
              setCollapsedExames(new Set(exames.map((_, i) => i)));
            }
          };

          const statusPills = [
            { id: 'todos', label: 'Todos', count: exames.length, color: '#555', bg: '#f5f5f5' },
            ...Object.entries(situacaoExameMap)
              .filter(([code]) => statusCount[code])
              .map(([code, label]) => ({
                id: code,
                label,
                count: statusCount[code],
                color: (situacaoExameCores[code] || {}).color || '#555',
                bg: (situacaoExameCores[code] || {}).bg || '#f5f5f5',
              })),
          ];

          const coletaPills = [
            { id: 'todos', label: 'Todos', count: exames.length },
            { id: 'colhido', label: 'Colhido', count: colhidos, color: '#2e7d32', bg: '#e8f5e9' },
            { id: 'nao_colhido', label: 'Nao colhido', count: naoColhidos, color: '#c62828', bg: '#fce4ec' },
          ];

          return (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '10px',
              padding: '14px 16px', backgroundColor: '#f8f9fa', borderRadius: '8px',
              marginBottom: '16px', border: '1px solid #eee',
            }}>
              {/* Status pills */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: '600', minWidth: '50px' }}>Status:</span>
                {statusPills.map(p => {
                  const isActive = filtroStatus === p.id;
                  return (
                    <button key={p.id} onClick={() => setFiltroStatus(p.id)} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '4px 12px', fontSize: '0.75rem', fontWeight: isActive ? '600' : '400',
                      borderRadius: '14px', cursor: 'pointer', transition: 'all 0.2s', border: 'none',
                      backgroundColor: isActive ? (p.bg || '#e3f2fd') : '#fff',
                      color: isActive ? (p.color || '#1565c0') : '#666',
                      boxShadow: isActive ? `0 0 0 1.5px ${p.color || '#1565c0'}` : '0 0 0 1px #ddd',
                    }}>
                      {p.label}
                      <span style={{
                        fontSize: '0.68rem', fontWeight: '700', minWidth: '16px', textAlign: 'center',
                        padding: '1px 5px', borderRadius: '8px',
                        backgroundColor: isActive ? (p.color || '#1565c0') : '#e0e0e0',
                        color: isActive ? '#fff' : '#888',
                      }}>{p.count}</span>
                    </button>
                  );
                })}
              </div>
              {/* Coleta pills */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: '600', minWidth: '50px' }}>Coleta:</span>
                {coletaPills.map(p => {
                  const isActive = filtroColeta === p.id;
                  return (
                    <button key={p.id} onClick={() => setFiltroColeta(p.id)} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '4px 12px', fontSize: '0.75rem', fontWeight: isActive ? '600' : '400',
                      borderRadius: '14px', cursor: 'pointer', transition: 'all 0.2s', border: 'none',
                      backgroundColor: isActive ? (p.bg || '#e3f2fd') : '#fff',
                      color: isActive ? (p.color || '#1565c0') : '#666',
                      boxShadow: isActive ? `0 0 0 1.5px ${p.color || '#1565c0'}` : '0 0 0 1px #ddd',
                    }}>
                      {p.label}
                      <span style={{
                        fontSize: '0.68rem', fontWeight: '700', minWidth: '16px', textAlign: 'center',
                        padding: '1px 5px', borderRadius: '8px',
                        backgroundColor: isActive ? (p.color || '#1565c0') : '#e0e0e0',
                        color: isActive ? '#fff' : '#888',
                      }}>{p.count}</span>
                    </button>
                  );
                })}
                {/* Collapse/Expand all */}
                <button onClick={toggleCollapseAll} style={{
                  marginLeft: 'auto', padding: '4px 12px', fontSize: '0.75rem',
                  borderRadius: '6px', cursor: 'pointer', border: '1px solid #ddd',
                  backgroundColor: '#fff', color: '#555',
                }}>
                  {collapsedExames.size > 0 ? 'Expandir Todos' : 'Colapsar Todos'}
                </button>
              </div>
            </div>
          );
        })()}

        {resultado.exames && resultado.exames.map((exame, idx) => {
          // Aplicar filtros
          const sit = (exame.SITUACAO || '').trim() || 'P';
          if (filtroStatus !== 'todos' && sit !== filtroStatus) return null;
          const colhido = (exame.MATERIALCOLHIDO || '').trim() === 'T';
          if (filtroColeta === 'colhido' && !colhido) return null;
          if (filtroColeta === 'nao_colhido' && colhido) return null;

          const isCollapsed = collapsedExames.has(idx);
          const toggleCollapse = () => {
            setCollapsedExames(prev => {
              const next = new Set(prev);
              if (next.has(idx)) next.delete(idx); else next.add(idx);
              return next;
            });
          };

          return (
          <div key={idx} className="exam-section">
            <div className="exam-header" onClick={toggleCollapse} style={{ cursor: 'pointer', userSelect: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '20px', height: '20px', fontSize: '0.7rem', color: '#999',
                  transition: 'transform 0.2s', transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                  flexShrink: 0,
                }}>&#9654;</span>
                <h2 className="exam-title">{limparNomeExame(exame.EXAME_NOME || exame.CODIGO_EXAME)}</h2>
                {(exame.MATERIALCOLHIDO || '').trim() === 'T' ? (
                  <span style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
                    backgroundColor: '#e8f5e9', color: '#2e7d32'
                  }}>
                    Colhido
                  </span>
                ) : (
                  <span style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
                    backgroundColor: '#fce4ec', color: '#c62828'
                  }}>
                    Nao colhido
                  </span>
                )}
                {['D','L','I','E','A'].includes((exame.SITUACAO || '').trim()) ? (
                  <span style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
                    backgroundColor: '#e8f5e9', color: '#2e7d32'
                  }}>
                    {situacaoExameLabel(exame.SITUACAO)}
                  </span>
                ) : (
                  <span style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
                    backgroundColor: '#fff8e1', color: '#e65100'
                  }}>
                    {situacaoExameLabel(exame.SITUACAO)}
                  </span>
                )}
                {exame.TEM_PDF === 1 && (
                  <button
                    onClick={() => abrirPDFExame(exame.PDF_EXAME_ID)}
                    className="btn-pdf"
                    title="Ver PDF do exame (WMI)"
                  >
                    PDF WMI
                  </button>
                )}
              </div>
              <span className="exam-hash">
                {gerarHashVerificacao(`${resultado.PROTOCOLO}-${exame.CODIGO_EXAME}-${resultado.PACIENTE_ID}`)}
              </span>
            </div>

            {!isCollapsed && <>
            {(exame.METODO || exame.MATERIAL) && (
              <div className="exam-meta">
                {exame.METODO && (
                  <span className="exam-meta-item"><strong>Método:</strong> {exame.METODO}</span>
                )}
                {exame.MATERIAL && (
                  <span className="exam-meta-item"><strong>Material:</strong> {exame.MATERIAL}</span>
                )}
              </div>
            )}

            {exame.resultados && exame.resultados.length > 0 ? (
              <>
                {(() => {
                  const resultadosFiltrados = exame.resultados.filter(r => {
                    const componente = r.COMPONENTE_NOME || r.COMPONENTE_LAUDO || '';
                    const res = (r.RESULTADO || '').trim();
                    // Filtrar linhas separadoras (---- NOME ----)
                    if (r._IS_SEPARATOR) return false;
                    if (/^-{3,}\s*.+\s*-{3,}$/.test(componente.trim())) return false;
                    // Filtrar componente = código do exame
                    if (componente === exame.CODIGO_EXAME || res === exame.CODIGO_EXAME) return false;
                    // Filtrar resultados binários (PDF/imagem)
                    if (res === '[TEXTO]' || res === '[IMAGEM]') return false;
                    // Filtrar linhas completamente vazias (sem nome, sem resultado, sem unidade)
                    if (!componente.trim() && !res && !r.COMPONENTE_UNIDADE) return false;
                    return true;
                  });

                  // Agrupar componentes por seção
                  const grupos = {};
                  const grupoOrdem = [];
                  resultadosFiltrados.forEach(r => {
                    const grupo = r.AGRUPAMENTO || 'Resultado';
                    if (!grupos[grupo]) {
                      grupos[grupo] = [];
                      grupoOrdem.push(grupo);
                    }
                    grupos[grupo].push(r);
                  });

                  const temReferenciaGeral = !!(exame.VALOR_REFERENCIA_DETALHADO || exame.VALOR_REFERENCIA);
                  const temMultiplosGrupos = grupoOrdem.length > 1;
                  // Quando há múltiplos grupos, referência é por componente (ignorar ref geral)
                  const algumComponenteTemRef = resultadosFiltrados.some(r => r.VALOR_REFERENCIA_COMPONENTE && !r.TIPODESCRICAO);

                  return grupoOrdem.map((grupo, grupoIdx) => {
                    const componentes = grupos[grupo];

                    // Separar componentes de dados e observações (TIPODESCRICAO presente = obs/texto livre)
                    const componentesDados = componentes.filter(c => {
                      const nome = (c.COMPONENTE_NOME || '').trim();
                      const res = (c.RESULTADO || '').trim();
                      if (!nome && !res) return false;
                      if (c.TIPODESCRICAO) return false;
                      return true;
                    });

                    const observacoes = componentes.filter(c => {
                      if (!c.TIPODESCRICAO) return false;
                      const res = (c.RESULTADO || '').trim();
                      return !!res;
                    });

                    // Mostrar referência inline quando componentes têm ref própria
                    const grupoTemRef = componentesDados.some(c => c.VALOR_REFERENCIA_COMPONENTE);
                    const mostrarRefInline = grupoTemRef || (temMultiplosGrupos && algumComponenteTemRef);

                    if (componentesDados.length === 0 && observacoes.length === 0) return null;

                    return (
                    <div key={grupoIdx} style={{ marginBottom: '16px' }}>
                      {grupo !== 'Resultado' && (
                        <h3 className="result-group-title">{grupo}</h3>
                      )}

                      {componentesDados.length > 0 && (
                      <div className="results-table">
                        <table>
                          <thead>
                            <tr>
                              <th style={{ width: mostrarRefInline ? '35%' : '40%' }}>Componente</th>
                              <th style={{ width: mostrarRefInline ? '25%' : '40%' }}>Resultado</th>
                              {mostrarRefInline && (
                                <th style={{ width: '40%' }}>Valores de Referência</th>
                              )}
                              {!mostrarRefInline && (
                                <th style={{ width: '20%' }}>Unidade</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {componentesDados.map((comp, compIdx) => {
                              let resultadoLimpo = (comp.RESULTADO || '-').trim();
                              resultadoLimpo = resultadoLimpo.replace(/\s*\(0\)\s*$/, '');
                              const unidade = comp.COMPONENTE_UNIDADE || '';

                              return (
                                <tr key={compIdx}>
                                  <td><strong>{comp.COMPONENTE_NOME || comp.COMPONENTE_LAUDO || `Código ${comp.COMPONENTE}`}</strong></td>
                                  <td className="result-value">
                                    {resultadoLimpo}{mostrarRefInline && unidade ? <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '6px', fontSize: '0.82rem' }}>{unidade}</span> : ''}
                                  </td>
                                  {mostrarRefInline && (
                                    <td style={{ fontSize: '0.75rem', color: '#666', whiteSpace: 'pre-wrap' }}>
                                      {comp.VALOR_REFERENCIA_COMPONENTE || ''}
                                    </td>
                                  )}
                                  {!mostrarRefInline && (
                                    <td>{unidade}</td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      )}

                      {/* Observações da seção */}
                      {observacoes.length > 0 && (
                        <div className="exam-notes" style={{ marginTop: '4px' }}>
                          {observacoes.map((obs, i) => {
                            const nome = (obs.COMPONENTE_NOME || '').trim();
                            const res = (obs.RESULTADO || '').trim();
                            if (!res) return null;
                            return (
                              <p key={i} style={{ margin: '2px 0', fontSize: '0.85rem' }}>
                                {nome ? <><strong>{nome}</strong> {res}</> : res}
                              </p>
                            );
                          })}
                        </div>
                      )}

                      {/* Historical chart */}
                      {componentesDados.length === 1 && (() => {
                        const comp = componentesDados[0];
                        const resultadoLimpo = (comp.RESULTADO || '').trim().replace(/\s*\(0\)\s*$/, '');
                        const resultadoNumerico = !isNaN(parseFloat(resultadoLimpo.replace(',', '.'))) && resultadoLimpo !== '';

                        if (resultadoNumerico && resultado.PACIENTE_ID && comp.COMPONENTE) {
                          if (!historicos[comp.COMPONENTE]) {
                            fetchHistorico(resultado.PACIENTE_ID, comp.COMPONENTE);
                          }
                          return renderGrafico(comp.COMPONENTE, comp.COMPONENTE_NOME, comp.COMPONENTE_UNIDADE);
                        }
                        return null;
                      })()}
                    </div>
                    );
                  });
                })()}
              </>
            ) : (
              <div className="exam-pending">
                {situacaoExameLabel(exame.SITUACAO) || 'Resultado ainda não disponível'}
              </div>
            )}

            {(exame.VALOR_REFERENCIA_DETALHADO || exame.VALOR_REFERENCIA) && (() => {
              // Suprimir ref geral quando refs por componente já foram exibidas nas seções
              const res = (exame.resultados || []).filter(r => {
                const comp = (r.COMPONENTE_NOME || r.COMPONENTE_LAUDO || '').trim();
                if (/^-{3,}\s*.+\s*-{3,}$/.test(comp)) return false;
                if (r._IS_SEPARATOR) return false;
                return true;
              });
              const temRefComponente = res.some(r => r.VALOR_REFERENCIA_COMPONENTE && !r.TIPODESCRICAO);
              if (temRefComponente) return null;
              return (
                <div className="exam-reference">
                  <div className="exam-reference-label">Valor de Referência</div>
                  <pre>{exame.VALOR_REFERENCIA_DETALHADO || exame.VALOR_REFERENCIA}</pre>
                </div>
              );
            })()}

            {(exame.OBSERVACAO || exame.INFO_C_OBSERVACAO) && (
              <div className="exam-notes">
                <div className="exam-notes-label">Notas</div>
                <pre>{exame.INFO_C_OBSERVACAO || exame.OBSERVACAO}</pre>
              </div>
            )}

            {exame.LABORATORIO_APOIO && (
              <p className="exam-lab-apoio">
                Exame realizado no Laboratório {exame.LABORATORIO_APOIO}.
              </p>
            )}

            {exame.DATACOLETA && (
              <p className="exam-data-coleta">
                <strong>Data da Coleta:</strong> {formatDateTime(exame.DATACOLETA)}
              </p>
            )}

            {/* Responsavel Tecnico por exame */}
            {renderResponsavel(exame)}
            </>}

            {idx < resultado.exames.length - 1 && <hr className="exam-divider" />}
          </div>
          );
        })}

        <div className="disclaimer">
          <p>
            <strong>Atenção!</strong> Os resultados anteriores informados são fornecidos de forma a ajudar o clínico
            na avaliação da evolução do paciente. Porém, devem ser analisados com cautela, levando-se em consideração
            os dados clínicos, alimentação, medicamentos, estados patológicos e fisiológicos, bem como valores de
            referência da época, uma vez que os mesmos podem sofrer modificações devido a mudanças de kits, fabricante,
            metodologia, etc. Somente seu médico pode interpretá-los corretamente.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResultadoDetalhes;
